import { useEffect, useMemo, useState } from "react";
import { useApp } from "../../../context/AppState";
import {
  AI_RECOMMENDATION_GENERATION_MODE,
  buildPreviewAiRecommendationContext,
  formatAiRecommendationCopy,
  getAiRecommendationMeta,
  isRuntimeAiCopyHydrated,
} from "../../../../shared/ai-recommendation-copy.js";
import { fetchRuntimeAiRecommendationCopy } from "../../../utils/aiRecommendationCopyRuntime.js";
import { isHybridStorefrontRuntime } from "../../../utils/popup-runtime-content-loading.js";

/**
 * Resolves AI banner text — static fallback or runtime generation at popup show.
 * @param {object | null} bannerEntry { section, index } from indexAiRecommendationUpsellSections
 * @param {object} popupData
 */
export default function useAiRecommendationCopy(bannerEntry, popupData) {
  const { appState, cartData } = useApp();
  const section = bannerEntry?.section;
  const sectionIdentity = useMemo(() => {
    if (!section) return "";
    const meta = getAiRecommendationMeta(section);
    return [
      bannerEntry?.index ?? "",
      section.type ?? "",
      section.text ?? "",
      meta.generationMode ?? "",
      meta.labelLine ?? "",
      meta.maxWords ?? "",
      JSON.stringify(meta.storageKeys ?? []),
      JSON.stringify(meta.contextSources ?? []),
    ].join("|");
  }, [
    bannerEntry?.index,
    section?.type,
    section?.text,
    section?.setting?.aiRecommendation,
  ]);
  const aiMeta = useMemo(
    () => getAiRecommendationMeta(section),
    [sectionIdentity]
  );
  const isPreviewMode = Boolean(appState?.is_preview_mode);

  const [runtimeText, setRuntimeText] = useState(null);
  const [loading, setLoading] = useState(false);

  const staticText = String(section?.text || "").trim();
  const storeName =
    appState?.shop?.shop || popupData?.shop?.shop || popupData?.storeName;
  const storefrontHydrated = !isPreviewMode && isRuntimeAiCopyHydrated(section);
  const hybridStorefront = !isPreviewMode && isHybridStorefrontRuntime();

  useEffect(() => {
    if (!section) return;

    if (storefrontHydrated) {
      setRuntimeText(staticText || null);
      setLoading(false);
      return;
    }

    if (hybridStorefront) {
      setRuntimeText(staticText || null);
      setLoading(
        aiMeta.generationMode !== AI_RECOMMENDATION_GENERATION_MODE.STATIC
      );
      return;
    }

    if (aiMeta.generationMode === AI_RECOMMENDATION_GENERATION_MODE.STATIC) {
      setRuntimeText(null);
      setLoading(false);
      return;
    }
    if (!storeName) return;

    let cancelled = false;
    setLoading(true);

    const runtimeContext = isPreviewMode
      ? buildPreviewAiRecommendationContext(popupData, {
          storageKeys: aiMeta.storageKeys,
          cart: cartData || window.__weupsellCart,
        })
      : {
          cart: cartData || window.__weupsellCart,
          currentProduct: window.__weupsellProduct,
          page: {
            path: typeof window !== "undefined" ? window.location.pathname : "",
            url: typeof window !== "undefined" ? window.location.href : "",
            referrer: typeof document !== "undefined" ? document.referrer : "",
            title: typeof document !== "undefined" ? document.title : "",
          },
        };

    fetchRuntimeAiRecommendationCopy({
      storeName,
      section,
      popupData,
      runtimeContext,
    })
      .then((result) => {
        if (!cancelled && result?.text) {
          setRuntimeText(String(result.text).trim());
        }
      })
      .catch(() => {
        if (!cancelled) setRuntimeText(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    section,
    sectionIdentity,
    storeName,
    popupData?._id,
    aiMeta.generationMode,
    aiMeta.contextSources,
    aiMeta.storageKeys,
    storefrontHydrated,
    hybridStorefront,
    isPreviewMode,
    cartData,
    staticText,
  ]);

  const displayText = useMemo(() => {
    let raw = "";
    if (aiMeta.generationMode === AI_RECOMMENDATION_GENERATION_MODE.STATIC) {
      raw = staticText;
    } else {
      raw = runtimeText || staticText;
    }
    return formatAiRecommendationCopy(raw, aiMeta.labelLine, aiMeta.maxWords);
  }, [
    aiMeta.generationMode,
    aiMeta.labelLine,
    aiMeta.maxWords,
    runtimeText,
    staticText,
  ]);

  return { displayText, loading, aiMeta };
}
